import { explanationDraftSchema } from '@mio/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';

import { ApiRequestError } from '@/shared/api/api-error';
import { apiRequest } from '@/shared/api/client';
import { workspaceQuery } from '@/shared/api/queries/workspace';

import { ExplanationDraft } from './explanation-draft';
import { pendingExplanation } from './pending-explanation';

import type { DraftVersion } from './explanation-draft';

export function useExplanationDraft(
  attemptId: string | null,
  server: DraftVersion | null,
  blocked: boolean,
  ownerId: string | null = null,
) {
  const client = useQueryClient();
  const drafts = useRef(
    new Map<
      string,
      {
        ownerId: string | null;
        draft: ExplanationDraft;
        writer: ReturnType<ReturnType<typeof pendingExplanation>['writer']> | null;
      }
    >(),
  );
  const key = JSON.stringify([ownerId, attemptId]);
  const entry = useMemo(() => {
    const existing = drafts.current.get(key);
    if (existing) return existing;
    if (!server)
      return {
        ownerId,
        writer: null,
        draft: new ExplanationDraft({ text: '', revision: 0 }, () =>
          Promise.reject(new Error('Wait for the saved explanation to load before editing.')),
        ),
      };
    const recovery = pendingExplanation(ownerId, attemptId);
    const writer = recovery.writer();
    const created = new ExplanationDraft(
      server,
      async (draft) => {
        if (!writer.current())
          throw new Error('This explanation is open in a newer page. Continue saving there.');
        if (!attemptId) throw new Error('Open an activity before writing an explanation.');
        let saved: DraftVersion;
        try {
          saved = await apiRequest(
            `/workshop/attempts/${attemptId}/draft`,
            explanationDraftSchema,
            {
              method: 'PATCH',
              body: { text: draft.text, expectedRevision: draft.revision },
            },
          );
        } catch (error) {
          if (!writer.current()) throw error;
          // A successful PATCH can lose its response. Confirm matching server evidence
          // before retrying; never overwrite a newer, different explanation blindly.
          const current = await client
            .fetchQuery({ ...workspaceQuery, staleTime: 0 })
            .catch(() => null);
          if (current?.workspace?.activeAttemptId === attemptId && current.draft) {
            if (current.draft.text === draft.text && current.draft.revision > draft.revision)
              saved = current.draft;
            else if (current.draft.revision > draft.revision)
              throw new ApiRequestError(
                409,
                'draft_changed',
                'Your explanation changed in another tab. Reload its saved version before retrying.',
              );
            else throw error;
          } else throw error;
        }
        if (saved.revision <= draft.revision)
          throw new Error(
            'The explanation save could not be confirmed. Keep this tab open and retry saving.',
          );
        if (writer.current())
          client.setQueryData(workspaceQuery.queryKey, (current) =>
            current?.workspace?.activeAttemptId === attemptId &&
            (current.draft?.revision ?? -1) <= saved.revision
              ? { ...current, draft: saved }
              : current,
          );
        return saved;
      },
      recovery.read(),
      (value) => writer.write(value),
    );
    const result = { ownerId, draft: created, writer };
    drafts.current.set(key, result);
    return result;
  }, [attemptId, client, key, ownerId, server]);
  const controller = entry.draft;
  const state = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  useEffect(() => {
    entry.writer?.activate();
    return () => entry.writer?.release();
  }, [entry]);
  useEffect(() => {
    if (server) controller.refresh(server);
    // Clear a recovered receipt after the server confirms identical saved text.
    if (server) entry.writer?.write(controller.pendingExplanation());
  }, [controller, entry.writer, server]);
  useEffect(() => {
    if (blocked || state.status !== 'dirty') return;
    const timer = setTimeout(() => {
      void controller.save().catch(() => undefined);
    }, 700);
    return () => clearTimeout(timer);
  }, [blocked, controller, state]);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (
        [...drafts.current.values()].some(
          (entry) => entry.ownerId === ownerId && entry.draft.getSnapshot().status !== 'saved',
        )
      )
        event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [ownerId]);
  return {
    ...state,
    hasOtherUnsavedDrafts: [...drafts.current.entries()].some(
      ([id, entry]) =>
        id !== key && entry.ownerId === ownerId && entry.draft.getSnapshot().status !== 'saved',
    ),
    edit: (text: string) => controller.edit(text),
    flush: () => controller.flush(),
    resolveConflict: (keepLocal: boolean) => controller.resolveConflict(keepLocal),
  };
}
